import { Component } from '@theme/component';
import { ThemeEvents, ReelSoundOnEvent } from '@theme/events';

/**
 * @typedef {Object} InstagramReelRefs
 * @property {HTMLVideoElement} [video] - The reel clip
 * @property {HTMLButtonElement} [sound] - The mute/unmute toggle
 * @property {HTMLButtonElement} [playback] - The play/pause toggle
 */

/**
 * One Instagram reel tile: it plays while on screen, and stays silent until asked.
 *
 * Six autoplaying clips would fetch megabytes the moment the page opens, so the
 * markup ships `preload="none"` and each clip is only loaded and played once it
 * actually scrolls into view — paused again on the way out, and while the tab is
 * hidden, because a backgrounded tab keeps decoding video unless told to stop.
 *
 * Autoplay is only permitted while muted, so sound is opt-in per tile. Turning
 * one on announces it on `document` and every other reel silences itself.
 *
 * @extends {Component<InstagramReelRefs>}
 */
export class InstagramReel extends Component {
  // No requiredRefs: a tile whose video is not picked yet still renders, and a missing required ref throws

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** Whether the tile is on screen, so a returning tab knows whether to resume. */
  #onScreen = false;

  /** Set when the visitor pauses the reel, so scrolling back or returning to the tab doesn't restart it. */
  #userPaused = false;

  /** @type {HTMLVideoElement | undefined} */
  #watchedVideo;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.reelSoundOn, this.#handleSoundElsewhere);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#arm();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.reelSoundOn, this.#handleSoundElsewhere);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#disarm();
  }

  /** A Section Rendering morph replaces the clip, so re-observe whatever is there now. */
  updatedCallback() {
    super.updatedCallback();

    this.#arm();
    this.#render();
  }

  /**
   * Turns this reel's sound on or off. Wired from the markup's `on:click`.
   */
  toggleSound() {
    const { video } = this.refs;
    if (!video) return;

    if (!video.muted) {
      video.muted = true;
      this.#render();
      return;
    }

    video.muted = false;
    // Turning sound on starts the clip, so a pause set earlier no longer applies
    this.#userPaused = false;
    this.#render();

    // A browser may still refuse to play it aloud, so fall back to silent rather than leave a lying icon
    video.play().catch(() => {
      video.muted = true;
      this.#render();
    });

    document.dispatchEvent(new ReelSoundOnEvent(this));
  }

  /**
   * Pauses or resumes this reel, and remembers the choice. Wired from the markup's `on:click`.
   */
  togglePlayback() {
    const { video } = this.refs;
    if (!video) return;

    if (video.paused) {
      this.#userPaused = false;
      if (video.preload === 'none') {
        video.preload = 'auto';
        video.load();
      }
      this.#play();
    } else {
      this.#userPaused = true;
      video.pause();
    }
  }

  #disarm() {
    this.#observer?.disconnect();
    this.#observer = undefined;

    this.#watchedVideo?.removeEventListener('play', this.#handlePlaybackChange);
    this.#watchedVideo?.removeEventListener('pause', this.#handlePlaybackChange);
    this.#watchedVideo = undefined;
  }

  #arm() {
    this.#disarm();

    const { video } = this.refs;
    if (!video) return;

    // The toggle mirrors what the clip is really doing, whoever started or stopped it
    video.addEventListener('play', this.#handlePlaybackChange);
    video.addEventListener('pause', this.#handlePlaybackChange);
    this.#watchedVideo = video;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      // No motion wanted, so load a single frame rather than leave an empty box
      video.preload = 'metadata';
      video.load();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#onScreen = entry.isIntersecting;

          if (!entry.isIntersecting) {
            video.pause();
            continue;
          }

          if (this.#userPaused) continue;

          if (video.preload === 'none') {
            video.preload = 'auto';
            video.load();
          }

          this.#play();
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(video);
    this.#observer = observer;
  }

  /** Deliberately leaves `muted` alone — a replay must not silence a reel the visitor turned up. */
  #play() {
    this.refs.video?.play().catch(() => {});
  }

  /** @param {Event} event */
  #handleSoundElsewhere = (event) => {
    if (!(event instanceof ReelSoundOnEvent) || event.reel === this) return;

    const { video } = this.refs;
    if (!video || video.muted) return;

    video.muted = true;
    this.#render();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.refs.video?.pause();
      return;
    }

    if (this.#onScreen && !this.#userPaused) this.#play();
  };

  #handlePlaybackChange = () => this.#render();

  /** Mirrors the clip's muted and playing state onto the toggles, which is what picks the glyphs in CSS. */
  #render() {
    const { video, sound, playback } = this.refs;
    if (!video) return;

    if (playback) {
      const playing = !video.paused;
      const playbackLabel = playing ? playback.dataset.labelPlaying : playback.dataset.labelPaused;

      playback.dataset.playing = String(playing);
      if (playbackLabel) playback.setAttribute('aria-label', playbackLabel);
    }

    if (!sound) return;

    const audible = !video.muted;
    const label = audible ? sound.dataset.labelAudible : sound.dataset.labelMuted;

    sound.setAttribute('aria-pressed', String(audible));
    if (label) sound.setAttribute('aria-label', label);
  }
}

if (!customElements.get('instagram-reel')) {
  customElements.define('instagram-reel', InstagramReel);
}
